import { Clock, Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import * as Dom from "foldkit/dom";
import * as Navigation from "foldkit/navigation";
import { evo } from "foldkit/struct";

import { settle, start } from "../../api/asyncData";
import {
	createNote,
	listFolders,
	listNotes,
	moveNote,
	renameFolder,
	searchNotes,
} from "../../api/commands/notes";
import { ApiError, toMessage } from "../../api/errors";
import type { FolderMove, NoteSummary } from "../../api/schemas/notes";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import {
	cleanFolder,
	folderOf,
	isUnder,
	joinFolder,
} from "../../widgets/folderTree";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";

/**
 * Knowledge: the circle's notes, a tag filter, and full-text search.
 *
 * KTD8: notes are not time-versioned, so every fetch is live and tagged by
 * generation only. While `Shared.asOf` is set the view shows a one-line
 * "live data" notice and hides "New note".
 *
 * Search is debounced here, not at the root: each keystroke issues a timer
 * Command whose `SearchDebounced` is dropped unless the text is unchanged.
 * A hit is kept only if it answers the current text and toggle, so a slow
 * older query can never overwrite a newer one.
 *
 * Folders: the tree, its counts, the Move menu and rename read the server's
 * `GET /notes/folders`, never the loaded page of notes, so a folder whose
 * notes are older than that page still shows and still renames on the
 * server. The route's `folder` fetches that folder and below (`under`).
 * A move or rename goes to the server, then both reload; links the move
 * made resolve differently are reported in `notice`. A folder made with New
 * folder lives only in `extraFolders` until a note is created or moved there.
 */

// ponytail: one page of 200 per folder; add offset paging when a folder outgrows it.
export const LIST_LIMIT = 200;
export const DEBOUNCE = "250 millis";

// COMMANDS

export const FetchNotes = Command.define("FetchKnowledgeNotes", {
	args: { generation: S.Number, folder: S.NullOr(S.String) },
	messages: [Message.CompletedFetchNotes],
	execute: ({ generation, folder }) =>
		Effect.all([
			Clock.currentTimeMillis,
			listNotes.run(
				folder === null
					? { limit: LIST_LIMIT }
					: { limit: LIST_LIMIT, under: folder },
			),
		]).pipe(
			Effect.map(([now, result]) =>
				Message.CompletedFetchNotes({ generation, folder, now, result }),
			),
		),
});
export const FetchFolders = Command.define("FetchKnowledgeFolders", {
	args: { generation: S.Number },
	messages: [Message.CompletedFetchFolders],
	execute: ({ generation }) =>
		listFolders
			.run({})
			.pipe(
				Effect.map((result) =>
					Message.CompletedFetchFolders({ generation, result }),
				),
			),
});
/**
 * Timer and search are keyed for interruption: newer text supersedes them
 * while they run. Nothing sends `Interrupt` today; the guards in `update`
 * drop a result that no longer matches the box, which is enough.
 */
export const DebounceSearch = Command.define("DebounceKnowledgeSearch", {
	args: { query: S.String },
	messages: [Message.SearchDebounced],
	interrupt: true,
	execute: ({ query }) =>
		Effect.sleep(DEBOUNCE).pipe(Effect.as(Message.SearchDebounced({ query }))),
});
export const Search = Command.define("SearchKnowledgeNotes", {
	args: { q: S.String, includeRecords: S.Boolean, generation: S.Number },
	messages: [Message.CompletedSearch],
	interrupt: true,
	execute: ({ q, includeRecords, generation }) =>
		searchNotes
			.run({ q, includeRecords })
			.pipe(
				Effect.map((result) =>
					Message.CompletedSearch({ q, includeRecords, generation, result }),
				),
			),
});
export const CreateNote = createNote.command(
	Message.CompletedCreate,
	({ generation, result }) => Message.CompletedCreate({ generation, result }),
);
export const MoveNote = moveNote.command(
	Message.CompletedMove,
	({ generation, folder, result }) =>
		Message.CompletedMove({ generation, folder, result }),
	{ generation: S.Number },
);
export const RenameFolder = renameFolder.command(
	Message.CompletedRename,
	({ generation, from, to, result }) =>
		Message.CompletedRename({ generation, from, to, result }),
	{ generation: S.Number },
);
export const Navigate = Command.define("NavigateFromKnowledge", {
	args: { url: S.String },
	messages: [Message.CompletedNavigate],
	execute: ({ url }) =>
		Navigation.pushUrl(url).pipe(Effect.as(Message.CompletedNavigate())),
});

export const initial: Model = {
	page: "Knowledge",
	notes: AsyncData.Idle(),
	folders: AsyncData.Idle(),
	now: 0,
	tag: Option.none(),
	query: "",
	includeRecords: false,
	search: AsyncData.Idle(),
	creating: false,
	error: Option.none(),
	folder: Option.none(),
	toggled: [],
	extraFolders: [],
	folderForm: Option.none(),
	moveMenu: Option.none(),
	dragging: Option.none(),
	dropTarget: Option.none(),
	moving: false,
	notice: Option.none(),
};

/** The Knowledge list for `folder` (null = all notes), keeping the current asOf. */
export const knowledgeHref = (shared: Shared, folder: string | null): string =>
	linkHref(
		shared.route,
		Route.Knowledge({
			asOf: Option.none(),
			folder: folder === null ? Option.none() : Option.some(folder),
		}),
	);

const routeFolder = (route: Route): Option.Option<string> =>
	route._tag === "Knowledge"
		? Option.filter(route.folder ?? Option.none(), (f) => cleanFolder(f) !== "")
		: Option.none();

type Return = PageReturn<Model, Message>;

/**
 * The first of "base", "base 2", "base 3"... no loaded note uses. The server
 * rejects a duplicate title case-insensitively (409 TitleTaken), so compare
 * lowercased. A note created elsewhere since the list loaded can still
 * collide; that 409 shows `knowledge.list.titleTaken`.
 */
export const freshTitle = (base: string, titles: ReadonlyArray<string>) => {
	const taken = new Set(titles.map((t) => t.toLowerCase()));
	const at = (n: number): string => {
		const title = n === 1 ? base : `${base} ${n}`;
		return taken.has(title.toLowerCase()) ? at(n + 1) : title;
	};
	return at(1);
};

const createErrorText = (error: ApiError): string =>
	ApiError.match(error, {
		Http: ({ status }) =>
			i18next.t(
				status === 409
					? "knowledge.list.titleTaken"
					: "knowledge.list.createError",
			),
		LimitExceeded: () => toMessage(error),
		NotFound: () => i18next.t("knowledge.list.createError"),
		Decode: () => i18next.t("knowledge.list.createError"),
		Transport: () => i18next.t("knowledge.list.createError"),
		Unauthenticated: () => i18next.t("knowledge.list.createError"),
		AiDisabled: () => i18next.t("knowledge.list.createError"),
	});

const notesIn = (model: Model): ReadonlyArray<NoteSummary> =>
	AsyncData.getOrElse(model.notes, () => ({ notes: [] })).notes;

const fetchNotes = (shared: Shared, folder: Option.Option<string>) =>
	FetchNotes({
		generation: shared.generation,
		folder: Option.getOrNull(folder),
	});

/** Refetches the shown notes and the folder tree. */
const reload = (model: Model, shared: Shared): Return => ({
	model: evo(model, { notes: start, folders: start }),
	commands: [
		fetchNotes(shared, model.folder),
		FetchFolders({ generation: shared.generation }),
	],
});

/**
 * Whether the server holds a note in `folder` or below. Unknown (the tree
 * has not loaded) counts as yes: the server answers 404 if it is empty.
 */
const holdsNotes = (model: Model, folder: string): boolean =>
	AsyncData.match(model.folders, {
		onIdle: () => true,
		onLoading: () => true,
		onFailure: () => true,
		onSuccess: ({ folders }) => folders.some((f) => isUnder(f.path, folder)),
		onRefreshing: ({ folders }) => folders.some((f) => isUnder(f.path, folder)),
		onStale: ({ data }) => data.folders.some((f) => isUnder(f.path, folder)),
	});

const navigateTo = (shared: Shared, folder: string | null) =>
	Navigate({ url: knowledgeHref(shared, folder) });

const failed = (model: Model, key: string): Return => ({
	model: evo(model, {
		moving: () => false,
		notice: () => Option.some({ text: i18next.t(key), error: true }),
	}),
});

/** "Moved." or, when links now resolve differently, which notes hold them. */
const moveNotice = (done: FolderMove, folder: string) =>
	done.changedLinks.length === 0
		? i18next.t("knowledge.folders.moved", {
				folder: folder || i18next.t("knowledge.folders.root"),
			})
		: i18next.t("knowledge.folders.linksChanged", {
				count: done.changedLinks.length,
				notes: [...new Set(done.changedLinks.map((l) => l.title))].join(", "),
			});

/** Starts moving note `id` to `folder`; a no-op if it is already there. */
const move = (
	model: Model,
	shared: Shared,
	id: string,
	folder: string,
): Return => {
	const note = notesIn(model).find((n) => n.id === id);
	const closed = evo(model, {
		moveMenu: () => Option.none(),
		dragging: () => Option.none(),
		dropTarget: () => Option.none(),
	});
	return !note || folderOf(note) === folder || model.moving
		? { model: closed }
		: {
				model: evo(closed, {
					moving: () => true,
					notice: () => Option.none(),
				}),
				commands: [MoveNote({ id, folder, generation: shared.generation })],
			};
};

/** Folder name input id; the form is inserted after load, so autofocus never fires. */
export const FOLDER_NAME_ID = "knowledge-folder-name";

/** Opening New folder / Rename moves focus into the name input (runs after the render commits). */
export const FocusFolderName = Command.define("FocusKnowledgeFolderName", {
	messages: [Message.CompletedFocusFolderName],
	execute: Dom.focus(`#${FOLDER_NAME_ID}`).pipe(
		Effect.as(Message.CompletedFocusFolderName()),
		Effect.catch(() => Effect.succeed(Message.CompletedFocusFolderName())),
	),
});

const submitFolderForm = (
	model: Model,
	shared: Shared,
	form: { mode: "create" | "rename"; value: string },
): Return => {
	const current = Option.getOrElse(model.folder, () => "");
	const closed = evo(model, { folderForm: () => Option.none() });
	// The submit button is never disabled (a disabled default button swallows
	// Enter), so an empty name or a write in flight is a no-op here instead.
	if (model.moving || form.value.trim() === "") return { model };
	if (form.mode === "create") {
		const path = joinFolder(current, form.value);
		return path === current
			? { model }
			: {
					model: evo(closed, {
						extraFolders: (fs) => (fs.includes(path) ? fs : [...fs, path]),
					}),
					commands: [navigateTo(shared, path)],
				};
	}
	const to = cleanFolder(form.value);
	if (to === current) return { model: closed };
	if (to === "" || isUnder(to, current))
		return failed(model, "knowledge.folders.renameInvalid");
	return holdsNotes(model, current)
		? {
				model: evo(closed, {
					moving: () => true,
					notice: () => Option.none(),
				}),
				commands: [
					RenameFolder({ from: current, to, generation: shared.generation }),
				],
			}
		: {
				// Nothing on the server to move: an empty folder is only local.
				model: evo(closed, {
					extraFolders: (fs) =>
						fs.map((f) =>
							isUnder(f, current) ? to + f.slice(current.length) : f,
						),
				}),
				commands: [navigateTo(shared, to)],
			};
};

const search = (model: Model, shared: Shared): Return => {
	const q = model.query.trim();
	return q === ""
		? { model: evo(model, { search: () => AsyncData.Idle() }) }
		: {
				model: evo(model, { search: start }),
				commands: [
					Search({
						q,
						includeRecords: model.includeRecords,
						generation: shared.generation,
					}),
				],
			};
};

export const page: Page<Model, Message> = {
	init: (route, shared) =>
		reload(evo(initial, { folder: () => routeFolder(route) }), shared),
	// Live data (KTD8): a new asOf changes nothing that is fetched; a new
	// folder fetches that folder's notes (the tree stays).
	changedRoute: (model, _previous, next, shared) => {
		const folder = routeFolder(next);
		const moved = evo(model, {
			folder: () => folder,
			folderForm: () => Option.none(),
			moveMenu: () => Option.none(),
		});
		return Option.getOrNull(folder) === Option.getOrNull(model.folder)
			? { model: moved }
			: {
					model: evo(moved, { notes: start }),
					commands: [fetchNotes(shared, folder)],
				};
	},
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedFetchNotes: ({ generation, folder, now, result }) =>
				generation !== shared.generation ||
				folder !== Option.getOrNull(model.folder)
					? { model }
					: {
							model: evo(model, {
								notes: (notes) => settle(notes, result),
								now: () => now,
							}),
						},
			CompletedFetchFolders: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: {
							model: evo(model, {
								folders: (folders) => settle(folders, result),
							}),
						},
			ChangedTag: ({ tag }) => ({
				model: evo(model, { tag: () => Option.fromNullishOr(tag) }),
			}),
			ChangedQuery: ({ value }) => {
				const next = evo(model, { query: () => value });
				const q = value.trim();
				return q === ""
					? { model: evo(next, { search: () => AsyncData.Idle() }) }
					: { model: next, commands: [DebounceSearch({ query: q })] };
			},
			SearchDebounced: ({ query }) =>
				query === model.query.trim() ? search(model, shared) : { model },
			ToggledIncludeRecords: () =>
				search(evo(model, { includeRecords: (on) => !on }), shared),
			CompletedSearch: ({ q, includeRecords, generation, result }) =>
				generation !== shared.generation ||
				q !== model.query.trim() ||
				includeRecords !== model.includeRecords
					? { model }
					: { model: evo(model, { search: (hits) => settle(hits, result) }) },
			ClickedNewNote: () =>
				model.creating || Option.isSome(shared.asOf)
					? { model }
					: {
							model: evo(model, {
								creating: () => true,
								error: () => Option.none(),
							}),
							commands: [
								CreateNote({
									title: freshTitle(
										i18next.t("knowledge.list.untitled"),
										AsyncData.getOrElse(model.notes, () => ({
											notes: [],
										})).notes.map((note) => note.title),
									),
									body: "",
									...Option.match(model.folder, {
										onNone: () => ({}),
										onSome: (folder) => ({ folder }),
									}),
									generation: shared.generation,
								}),
							],
						},
			CompletedCreate: ({ generation, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: ({ note }) => ({
								model: evo(model, { creating: () => false }),
								commands: [
									Navigate({
										url: linkHref(
											shared.route,
											Route.Note({ id: note.id, asOf: Option.none() }),
										),
									}),
								],
							}),
							onFailure: (error) => ({
								model: evo(model, {
									creating: () => false,
									error: () => Option.some(createErrorText(error)),
								}),
							}),
						}),
			CompletedNavigate: () => ({ model }),
			ToggledFolder: ({ path }) => ({
				model: evo(model, {
					toggled: (ts) =>
						ts.includes(path) ? ts.filter((t) => t !== path) : [...ts, path],
				}),
			}),
			ClickedNewFolder: () =>
				Option.isSome(shared.asOf)
					? { model }
					: {
							model: evo(model, {
								folderForm: () =>
									Option.some({ mode: "create" as const, value: "" }),
							}),
							commands: [FocusFolderName()],
						},
			ClickedRenameFolder: () =>
				Option.isSome(shared.asOf) || Option.isNone(model.folder)
					? { model }
					: {
							model: evo(model, {
								folderForm: () =>
									Option.some({
										mode: "rename" as const,
										value: Option.getOrElse(model.folder, () => ""),
									}),
							}),
							commands: [FocusFolderName()],
						},
			ChangedFolderName: ({ value }) => ({
				model: evo(model, {
					folderForm: (form) => Option.map(form, (f) => ({ ...f, value })),
				}),
			}),
			CompletedFocusFolderName: () => ({ model }),
			CancelledFolderForm: () => ({
				model: evo(model, { folderForm: () => Option.none() }),
			}),
			SubmittedFolderForm: () =>
				Option.match(model.folderForm, {
					onNone: () => ({ model }),
					onSome: (form) => submitFolderForm(model, shared, form),
				}),
			ClickedMove: ({ id }) => ({
				model: evo(model, {
					moveMenu: (open) =>
						Option.getOrNull(open) === id ? Option.none() : Option.some(id),
				}),
			}),
			ChoseFolder: ({ id, folder }) =>
				Option.isSome(shared.asOf)
					? { model }
					: move(model, shared, id, folder),
			StartedDrag: ({ id }) =>
				Option.isSome(shared.asOf)
					? { model }
					: { model: evo(model, { dragging: () => Option.some(id) }) },
			EndedDrag: () => ({
				model: evo(model, {
					dragging: () => Option.none(),
					dropTarget: () => Option.none(),
				}),
			}),
			EnteredFolder: ({ folder }) => ({
				model: evo(model, { dropTarget: () => Option.some(folder) }),
			}),
			LeftFolder: ({ folder }) => ({
				model: evo(model, {
					dropTarget: (t) =>
						Option.getOrNull(t) === folder ? Option.none() : t,
				}),
			}),
			DroppedOnFolder: ({ folder }) =>
				Option.match(model.dragging, {
					onNone: () => ({ model }),
					onSome: (id) => move(model, shared, id, folder),
				}),
			CompletedMove: ({ generation, folder, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: (done) =>
								reload(
									evo(model, {
										moving: () => false,
										notice: () =>
											Option.some({
												text: moveNotice(done, folder),
												error: false,
											}),
									}),
									shared,
								),
							onFailure: () => failed(model, "knowledge.folders.moveError"),
						}),
			CompletedRename: ({ generation, from, to, result }) =>
				generation !== shared.generation
					? { model }
					: Result.match(result, {
							onSuccess: (done) => {
								const moved = (f: string) =>
									isUnder(f, from) ? to + f.slice(from.length) : f;
								const next = reload(
									evo(model, {
										moving: () => false,
										extraFolders: (fs) => fs.map(moved),
										notice: () =>
											Option.some({ text: moveNotice(done, to), error: false }),
									}),
									shared,
								);
								return Option.match(model.folder, {
									onNone: () => next,
									onSome: (current) =>
										isUnder(current, from)
											? {
													...next,
													commands: [
														...(next.commands ?? []),
														navigateTo(shared, moved(current)),
													],
												}
											: next,
								});
							},
							onFailure: () => failed(model, "knowledge.folders.renameError"),
						}),
		}),
};
